import { createSelector } from '@reduxjs/toolkit';
import { AllDocumentsDomainMeta, AllDocumentsMeta } from 'reactApp/modules/allDocuments/allDocuments.constants';
import {
    type AllDocumentsCategory,
    type AllDocumentsCategoryType,
    type AllDocumentsItem,
    type IAllDocumentsFolder,
    type IAllDocumentsState,
    EAllDocumentsType,
} from 'reactApp/modules/allDocuments/allDocuments.types';
import { getFeatureAllDocuments } from 'reactApp/modules/features/features.selectors';
import type { HomeFile } from 'reactApp/modules/home/home.types';
import { getHomeList } from 'reactApp/modules/home/selector/getHomeList';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { RootState } from 'reactApp/store';
import type { Url } from 'reactApp/types/Tree';
import { sendXray } from 'reactApp/utils/ga';

const getAllDocumentsStore = (state: RootState): IAllDocumentsState => state.allDocuments;

export const getAllDocumentsCursor = (state: RootState) => getAllDocumentsStore(state).cursor;
export const getAllDocumentsCurrentType = (state: RootState) => getAllDocumentsStore(state).currentDocType;
export const getAllDocumentsCurrentSection = (state: RootState) => getAllDocumentsStore(state).currentSection;
export const getAllDocumentsPrevSection = (state: RootState) => getAllDocumentsStore(state).prevSection;
export const getAllDocumentsCategory = (state: RootState): AllDocumentsCategoryType => getAllDocumentsStore(state).currentCategory;
export const getAllDocumentsCategories = (state: RootState): AllDocumentsCategory[] => getAllDocumentsStore(state).categories;
export const getDocumentsState = createSelector(getAllDocumentsStore, (state) => {
    return {
        isLoaded: state.isLoaded,
        type: state.currentDocType,
        count: state.idxs.length,
        docsSize: state.docsSize,
        currentDocType: state.currentDocType,
        currentSection: state.currentSection,
        prevSection: state.prevSection,
    };
});

export const getCurrentAllDocumentsFolder = createSelector(getAllDocumentsStore, (state): IAllDocumentsFolder | undefined => {
    if (!state.currentDocType) {
        sendXray(['alldcs', 'dctp', 'nll']);
        return;
    }
    return {
        id: state.currentDocType,
        value: state.currentDocType,
        name: state.currentDocType,
        isLoaded: state.isLoaded,
        isLoading: state.isLoading,
        cursor: state.cursor,
        hasMoreToLoad: state.hasMoreToLoad,
        childs: state.idxs,
    };
});

export const getAllDocumentsItemById = createSelector(
    getHomeList,
    (state, id) => id,
    (list, id): AllDocumentsItem | undefined => {
        const homeItem = list[id] as HomeFile;

        if (!homeItem) {
            return;
        }

        return {
            ...homeItem,
            isReadOnly: homeItem.isReadOnly ?? false,
            url: homeItem.url ?? ({} as Url),
            storage: EStorageType.alldocuments,
        };
    }
);

export const getExtensionsForType = createSelector(
    getFeatureAllDocuments,
    (state, type: EAllDocumentsType | null | undefined) => type,
    (feature, type): string[] => {
        /**
         *  CLOUDWEB-14993
         *  Для корзины список расширений должен быть пустой
         */
        if (window.location.pathname === '/alldocuments/trashbin') {
            return [];
        }
        return feature?.[type || EAllDocumentsType.document] ?? [];
    }
);

export const getAllDocumentsExtensions = createSelector(getFeatureAllDocuments, (feature) => {
    return feature ? Object.values(feature).flat() : [];
});

export const getAllDocumentsBreadcrumbs = createSelector(getAllDocumentsCurrentType, (documentType) => {
    return [
        {
            name: AllDocumentsMeta[documentType || EAllDocumentsType.document].name,
            id: AllDocumentsMeta[documentType || EAllDocumentsType.document].id,
            kind: 'storage' as const,
            storage: EStorageType.alldocuments,
        },
    ];
});

export const getAllDocumentsDomainBreadcrumbs = createSelector(getAllDocumentsCurrentType, (documentType) => {
    return [
        {
            name: AllDocumentsDomainMeta[documentType || EAllDocumentsType.document].name,
            id: AllDocumentsDomainMeta[documentType || EAllDocumentsType.document].id,
            kind: 'storage' as const,
            storage: EStorageType.alldocuments,
        },
    ];
});

export const getAllDocumentsAccessRequestSent = createSelector(getAllDocumentsStore, (state) => state.allDocumentsAccessRequestSent);
